import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AgGridTable from '../../../components/AgGridTable';
import SidebarPanel from '../../../components/admin/SidebarPanel';
import EducationalTooltip from '../../../components/shared/EducationalTooltip';
import ModelDocumentationModal from '../../../components/shared/ModelDocumentationModal';
import MultiProfileManager from '../../../components/shared/MultiProfileManager';
import EditableUserDetails from '../../../components/admin/EditableUserDetails';
import MembershipManager from '../../../components/admin/MembershipManager';
import EHRProfileManager from '../../../components/admin/EHRProfileManager';
import SSOConfigManager from '../../../components/admin/SSOConfigManager';
import AuditLogs from '../../../components/shared/AuditLogs';
import PasswordInput from '../../../components/shared/PasswordInput';
import { adminApi } from '../../../services/api';
import { validatePassword } from '../../../utils/passwordValidation';

const HealthcareUsersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, tab } = useParams();
  const gridRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ specialty_id: '', health_system_id: '', is_active: 'true', search: '' });
  const [roles, setRoles] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [passwordData, setPasswordData] = useState({ password: '', confirmPasswordChange: false });
  const [savingPassword, setSavingPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', username: '', password: '',
    job_title: '', phone: '', is_active: true, specialty_ids: [],
    health_system_id: '', role_id: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchHealthSystems();
    fetchSpecialties();
  }, [filters]);

  useEffect(() => {
    if (location.state?.mode === 'create') {
      setIsCreateMode(true);
      setSelectedUser(null);
      setShowPanel(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (userId && users.length > 0) {
      const user = users.find(u => u.id == userId);
      if (user) handleRowClick({ data: user });
    }
  }, [userId, users]);

  // Sync tab parameter with activeTab state
  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    } else if (userId && !tab) {
      setActiveTab('overview');
    }
  }, [tab, userId]);

  // Refetch roles when health_system changes in create mode
  useEffect(() => {
    if (isCreateMode && formData.health_system_id) {
      fetchRoles(formData.health_system_id);
    } else if (isCreateMode && !formData.health_system_id) {
      fetchRoles();
    }
  }, [formData.health_system_id, isCreateMode]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { user_type: 'healthcare', ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)) };
      const res = await adminApi.getUsers(params);
      setUsers(res.data?.users || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async (healthSystemId = null) => {
    try {
      const params = { category: 'healthcare_user', is_active: 'true' };
      if (healthSystemId) {
        params.health_system = healthSystemId;
      }
      const res = await adminApi.getRoles(params);
      setRoles(res.data?.roles || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
      setRoles([]);
    }
  };

  const fetchHealthSystems = async () => {
    try {
      const res = await adminApi.getHealthSystems();
      setHealthSystems(res.data?.health_systems || []);
    } catch (err) {
      console.error('Failed to load health systems:', err);
    }
  };

  const fetchSpecialties = async () => {
    try {
      const res = await adminApi.getSpecialties();
      setSpecialties(res.data?.specialties || []);
    } catch (err) {
      console.error('Failed to load specialties:', err);
    }
  };

  const handleRowClick = useCallback((event) => {
    setIsCreateMode(false);
    setSelectedUser(event.data);
    setShowPanel(true);
    setActiveTab('overview');
    navigate(`/admin/users/healthcare/${event.data.id}/overview`, { replace: true });
  }, [navigate]);

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    if (selectedUser) {
      navigate(`/admin/users/healthcare/${selectedUser.id}/${newTab}`, { replace: true });
    }
  }, [navigate, selectedUser]);

  const handleClosePanel = () => {
    setShowPanel(false);
    setSelectedUser(null);
    setIsCreateMode(false);
    setFormData({
      first_name: '', last_name: '', email: '', username: '', password: '',
      job_title: '', phone: '', is_active: true, specialty_ids: [],
      health_system_id: '', role_id: ''
    });
    setPasswordData({ password: '', confirmPasswordChange: false });
    navigate('/admin/users/healthcare', { replace: true });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      alert('Validation Error: ' + passwordValidation.error);
      return;
    }

    try {
      // Convert empty strings to null for UUID fields
      const payload = {
        ...formData,
        user_type: 'healthcare',
        specialty_ids: formData.specialty_ids.length > 0 ? formData.specialty_ids : undefined,
        // Remove health_system_id and role_id from payload, add to memberships array instead
        health_system_id: undefined,
        role_id: undefined
      };

      // If health_system and role are selected, create membership
      if (formData.health_system_id && formData.role_id) {
        payload.health_system_memberships = [{
          health_system_id: formData.health_system_id,
          role_id: formData.role_id
        }];
      }

      await adminApi.post('/admin/users/', payload);
      fetchUsers();
      handleClosePanel();
      alert('Healthcare user created successfully');
    } catch (err) {
      alert('Failed to create user: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSavePassword = async () => {
    try {
      setSavingPassword(true);

      // Validate password
      if (passwordData.password && passwordData.password.trim() !== '') {
        const passwordValidation = validatePassword(passwordData.password);
        if (!passwordValidation.isValid) {
          alert('Validation Error: ' + passwordValidation.error);
          setSavingPassword(false);
          return;
        }

        if (!passwordData.confirmPasswordChange) {
          alert('Confirmation Required: Please confirm the password change by checking the confirmation box');
          setSavingPassword(false);
          return;
        }

        // Update password
        const response = await adminApi.put(`/admin/users/healthcare/${selectedUser.id}/`, { password: passwordData.password });

        if (response.data?.success) {
          alert('Password updated successfully');
          setPasswordData({ password: '', confirmPasswordChange: false });
        } else {
          throw new Error(response.data?.message || 'Failed to update password');
        }
      }
    } catch (err) {
      alert('Failed to update password: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCellValueChanged = async (event) => {
    try {
      const { data, colDef, newValue } = event;
      await adminApi.updateUser(data.id, { [colDef.field]: newValue });
      fetchUsers();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.message || err.message));
      fetchUsers();
    }
  };

  const handleExport = () => {
    gridRef.current?.api.exportDataAsCsv({ fileName: `healthcare-users-${new Date().toISOString().split('T')[0]}.csv` });
  };

  const columnDefs = [
    { headerName: 'Name', valueGetter: p => `${p.data?.first_name || ''} ${p.data?.last_name || ''}`, width: 150 },
    { headerName: 'Email', field: 'email', width: 200, editable: true },
    { headerName: 'Username', field: 'username', width: 130 },
    { headerName: 'Job Title', field: 'job_title', width: 130 },
    {
      headerName: 'Specialties',
      valueGetter: p => p.data?.specialties?.map(s => s.name).join(', ') || '-',
      width: 180
    },
    {
      headerName: 'Health Systems',
      valueGetter: p => {
        const count = p.data?.health_systems?.length || 0;
        return count > 0 ? `${count} system${count > 1 ? 's' : ''}` : '-';
      },
      width: 120
    },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: p => (
        <span className={`badge bg-${p.data?.is_active ? 'success' : 'danger'}`}>
          {p.data?.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <>
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
          <h4 className="text-lg">
            Healthcare Users
          </h4>
          <p className="text-muted">Doctors, nurses, and clinical staff. Each can have multiple EHR/SSO profiles per health system</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
              <li className="breadcrumb-item"><a href="">User Management</a></li>
              <li className="breadcrumb-item active">Healthcare Users</li>
            </ol>
          </nav>
        </div>
      </div>
      <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
        <button className="btn btn-primary" onClick={() => navigate('/admin/users/healthcare', { state: { mode: 'create' } })}>
          <i className="fas fa-plus me-1" />Add Healthcare User
        </button>
      </div>
      <div className="card">
        <div className="d-flex justify-content-between mb-3 p-2">
          <div className='d-flex gap-2'>
            <select className="form-select form-select-sm" value={filters.health_system_id} onChange={e => setFilters({...filters, health_system_id: e.target.value})}>
              <option value="">All Health Systems</option>
              {healthSystems.map(hs => <option key={hs.health_system_id} value={hs.id}>{hs.name}</option>)}
            </select>
            <select className="form-select form-select-sm" value={filters.is_active} onChange={e => setFilters({...filters, is_active: e.target.value})}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <input type="text" className="form-control form-control-sm" placeholder="Search..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
            <vr></vr>
            <small className="text-muted text-nowrap align-self-center">
              Showing {users.length} user{users.length !== 1 ? 's' : ''}
            </small>
          </div>
          <div className='d-flex gap-2 justify-content-end'>
            <button className="btn btn-sm btn-info" onClick={() => setShowDocsModal(true)}>
              <i className="fas fa-book me-1" />Docs
            </button>
            <button className="btn btn-sm btn-success" onClick={handleExport}>
              <i className="fas fa-file-export me-1" />Export
            </button>
          </div>
        </div>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <AgGridTable ref={gridRef} rowData={users} columnDefs={columnDefs} onRowClicked={handleRowClick} onCellValueChanged={handleCellValueChanged} />
        )}
      </div>

      <SidebarPanel isOpen={showPanel} onClose={handleClosePanel} title={isCreateMode ? "Create Healthcare User" : "Healthcare User Details"}>
        {isCreateMode ? (
          <div className="card">
            <div className="card-body p-3">
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label small mb-1">First Name *</label>
                  <input type="text" className="form-control form-control-sm" required value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Last Name *</label>
                  <input type="text" className="form-control form-control-sm" required value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label small mb-1">Email *</label>
                  <input type="email" className="form-control form-control-sm" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Username *</label>
                  <input type="text" className="form-control form-control-sm" required value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12">
                  <PasswordInput
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}
                    required
                    label="Password"
                    showRequirements
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label small mb-1">Job Title</label>
                  <input type="text" className="form-control form-control-sm" value={formData.job_title} onChange={e => setFormData({...formData, job_title: e.target.value})} />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Phone</label>
                  <input type="tel" className="form-control form-control-sm" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-12">
                  <label className="form-label small mb-1">Specialties</label>
                  <select multiple className="form-select form-select-sm" style={{minHeight: '80px'}} value={formData.specialty_ids} onChange={e => setFormData({...formData, specialty_ids: Array.from(e.target.selectedOptions, option => option.value)})}>
                    {specialties.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <small className="text-muted">Hold Ctrl/Cmd to select multiple</small>
                </div>
              </div>

              <hr className="my-3" />
              <h6 className="small fw-bold text-muted mb-2">Health System Access (Optional)</h6>

              <div className="row mb-3">
                <div className="col-12">
                  <label className="form-label small mb-1">Health System</label>
                  <select
                    className="form-select form-select-sm"
                    value={formData.health_system_id}
                    onChange={e => setFormData({...formData, health_system_id: e.target.value, role_id: ''})}
                  >
                    <option value="">No Health System</option>
                    {healthSystems.map(hs => (
                      <option key={hs.id} value={hs.id}>{hs.name}</option>
                    ))}
                  </select>
                  <small className="text-muted">Optional: Select health system to auto-create membership</small>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <label className="form-label small mb-1">Role {formData.health_system_id && '*'}</label>
                  <select
                    className="form-select form-select-sm"
                    required={!!formData.health_system_id}
                    value={formData.role_id}
                    onChange={e => setFormData({...formData, role_id: e.target.value})}
                  >
                    <option value="">Select Role</option>
                    {roles.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  {formData.health_system_id && (
                    <small className="text-muted">Required when health system is selected</small>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label small mb-1">Status</label>
                  <select className="form-select form-select-sm" value={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.value === 'true'})}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <button type="button" className="btn btn-primary btn-sm me-2" onClick={handleCreateUser}>Create User</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleClosePanel}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        ) : selectedUser && (
          <>
            <ul className="nav nav-tabs nav-tabs-custom mb-2">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => handleTabChange('overview')}>Overview</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'memberships' ? 'active' : ''}`} onClick={() => handleTabChange('memberships')}>Memberships</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'ehr' ? 'active' : ''}`} onClick={() => handleTabChange('ehr')}>EHR Profiles</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'sso' ? 'active' : ''}`} onClick={() => handleTabChange('sso')}>SSO Configs</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'audit-logs' ? 'active' : ''}`} onClick={() => handleTabChange('audit-logs')}>Audit Trail</button>
              </li>
            </ul>

            {activeTab === 'overview' && (
              <>
              <div className="card">
                <div className="card-body p-3">
                  <EditableUserDetails
                    userData={selectedUser}
                    userId={selectedUser.id}
                    userType="healthcare"
                    fieldConfig={[
                      { name: 'first_name', label: 'First Name', type: 'text', editable: true },
                      { name: 'last_name', label: 'Last Name', type: 'text', editable: true },
                      { name: 'email', label: 'Email', type: 'email', editable: true },
                      { name: 'username', label: 'Username', type: 'text', editable: false },
                      { name: 'job_title', label: 'Job Title', type: 'text', editable: true },
                      { name: 'phone', label: 'Phone', type: 'text', editable: true },
                      { name: 'id', label: 'Ankr ID', type: 'text', editable: false },
                      { name: 'is_active', label: 'Active', type: 'checkbox', editable: true },
                      { name: 'created_at', label: 'Created At', type: 'date', editable: false },
                    ]}
                    onSave={() => {
                      fetchUsers();
                      alert('User updated successfully!');
                    }}
                    onError={(err) => {
                      console.error('Error updating user:', err);
                    }}
                  />
                </div>
              </div>
              <div className="card mt-3">
                <div className="card-body p-3">
                  <h6 className="mb-3">Password Management</h6>
                  <div className="row">
                    <div className="col-12">
                      <PasswordInput
                        value={passwordData.password}
                        onChange={e => { setPasswordData({...passwordData, password: e.target.value, confirmPasswordChange: false}); }}
                        required={false}
                        label="New Password"
                        placeholder="Leave blank to keep current password"
                        showRequirements
                        className="mb-2"
                      />
                      {passwordData.password && passwordData.password.trim() !== '' && (
                        <div className="form-check mt-2 mb-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="confirmPasswordChangeHealthcare"
                            checked={passwordData.confirmPasswordChange}
                            onChange={e => setPasswordData({...passwordData, confirmPasswordChange: e.target.checked})}
                          />
                          <label className="form-check-label small text-muted" htmlFor="confirmPasswordChangeHealthcare">
                            I want to update the user&apos;s password. This will not affect their existing sessions. They can continue using Ankr till next login.
                          </label>
                        </div>
                      )}
                      {passwordData.password && passwordData.password.trim() !== '' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleSavePassword}
                          disabled={savingPassword}
                        >
                          {savingPassword ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1"></span>
                              Updating Password...
                            </>
                          ) : (
                            'Update Password'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </>
            )}

            {activeTab === 'memberships' && (
              <MembershipManager
                userId={selectedUser.id}
                onUpdate={() => {
                  fetchUsers();
                  // Refetch selected user details to update sidebar
                  const user = users.find(u => u.id == selectedUser.id);
                  if (user) setSelectedUser(user);
                }}
              />
            )}

            {activeTab === 'ehr' && (
              <EHRProfileManager
                userId={selectedUser.id}
                onUpdate={() => {
                  fetchUsers();
                  const user = users.find(u => u.id == selectedUser.id);
                  if (user) setSelectedUser(user);
                }}
              />
            )}

            {activeTab === 'sso' && (
              <SSOConfigManager
                userId={selectedUser.id}
                onUpdate={() => {
                  fetchUsers();
                  const user = users.find(u => u.id == selectedUser.id);
                  if (user) setSelectedUser(user);
                }}
              />
            )}

            {activeTab === 'audit-logs' && (
              <div className="card">
                <div className="card-body p-3">
                  <AuditLogs
                    resourceType="user"
                    userType="healthcare"
                    resourceId={selectedUser.id}
                    title="User Audit Trail"
                    showHeader={true}
                    pageSize={25}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </SidebarPanel>

      <ModelDocumentationModal show={showDocsModal} onHide={() => setShowDocsModal(false)} modelType="healthcare_user" />
    </>
  );
};

export default HealthcareUsersPage;
